import { Injectable } from '@angular/core';
import { API_BASE } from '../../../constants/general.constants';

/**
 * WasmModuleService — loads and executes jco-transpiled WASM modules
 * in the browser using the instantiation API.
 *
 * Host imports connect modules to Enlighten's data layer:
 * - host-files: per-module in-memory file store (browser has no direct FS)
 * - host-variables: shared variable store (populated from workflow variables)
 * - host-arrow: per-module in-memory Arrow IPC store
 * - host-log: configurable log callback (defaults to console)
 * - host-api: synchronous service calls via sync XHR proxy
 *
 * See: ehq-wilkins/dev-docs/guides/global-service-protocol.md
 */

/** Manifest returned by a module's getManifest() */
export interface ModuleManifest {
  name: string;
  version: string;
  description?: string;
  functions: ModuleFunction[];
}

export interface ModuleFunction {
  name: string;
  description?: string;
  inputs: { name: string; type: string; required?: boolean; default?: unknown; description?: string }[];
  outputs: { name: string; type: string; description?: string }[];
}

/** A loaded WASM module instance */
export interface LoadedModule {
  manifest: ModuleManifest;
  getManifest: () => string;
  execute: (functionName: string, argsJson: string) => string;
  /** In-memory Arrow IPC table store (host-arrow) */
  tables: Map<string, Uint8Array>;
  /** In-memory file store (host-files) */
  files: Map<string, Uint8Array>;
}

/** Log callback type for host-log */
export type WasmLogCallback = (level: string, message: string) => void;

/** Minimal WASI stubs for the WASM Component Model runtime */
function createWasiStubs(): Record<string, unknown> {
  class OutputStream {
    write(_data: unknown) { return { tag: 'ok', val: undefined }; }
    blockingFlush() { /* intentional no-op: WASI stub */ }
  }
  class InputStream {
    read(_len: unknown) { return new Uint8Array(0); }
    blockingRead(_len: unknown) { return new Uint8Array(0); }
  }
  class TerminalInput { /* intentional: WASI stub class */ }
  class TerminalOutput { /* intentional: WASI stub class */ }
  class Pollable {
    block() { /* intentional no-op: WASI stub */ }
    ready() { return true; }
  }

  return {
    'wasi:cli/environment': { getEnvironment() { return []; }, getArguments() { return []; } },
    'wasi:cli/exit': { exit(_s: unknown) {} },
    'wasi:cli/stderr': { getStderr() { return new OutputStream(); } },
    'wasi:cli/stdin': { getStdin() { return new InputStream(); } },
    'wasi:cli/stdout': { getStdout() { return new OutputStream(); } },
    'wasi:cli/terminal-input': { TerminalInput },
    'wasi:cli/terminal-output': { TerminalOutput },
    'wasi:cli/terminal-stderr': { getTerminalStderr() { return undefined; } },
    'wasi:cli/terminal-stdin': { getTerminalStdin() { return undefined; } },
    'wasi:cli/terminal-stdout': { getTerminalStdout() { return undefined; } },
    'wasi:io/error': { Error: class { toDebugString() { return 'io-error'; } } },
    'wasi:io/poll': { Pollable, poll(_l: unknown) { return [0]; } },
    'wasi:io/streams': { InputStream, OutputStream },
    'wasi:random/insecure-seed': { insecureSeed() { return [BigInt(42), BigInt(0)]; } },
    'wasi:clocks/monotonic-clock': {
      now() { return BigInt(0); },
      resolution() { return BigInt(1000000); },
      subscribeDuration(_d: unknown) { return new Pollable(); },
      subscribeInstant(_i: unknown) { return new Pollable(); },
    },
    'wasi:clocks/wall-clock': {
      now() { return { seconds: BigInt(Math.floor(Date.now() / 1000)), nanoseconds: 0 }; },
      resolution() { return { seconds: BigInt(1), nanoseconds: 0 }; },
    },
  };
}

@Injectable({
  providedIn: 'root',
})
export class WasmModuleService {
  private readonly modules = new Map<string, LoadedModule>();
  private readonly moduleCache = new Map<string, WebAssembly.Module>();

  /** Shared workflow variables store — populated by the binding layer from NotebookVariablesService */
  readonly variables = new Map<string, string>();

  /** Log callback — defaults to console, can be set by step execution context */
  logCallback: WasmLogCallback = (level: string, message: string) => {
    const prefix = `[WASM ${level}]`;
    switch (level) {
      case 'error': console.error(prefix, message); break;
      case 'warn': console.warn(prefix, message); break;
      default: console.log(prefix, message);
    }
  };

  /**
   * Service call function for host-api::call(). Set by the binding layer
   * to provide auth context. Uses sync XHR because WIT imports must be synchronous.
   * See: ehq-wilkins/dev-docs/guides/global-service-protocol.md
   */
  serviceCallFn: ((url: string, argsJson: string) => string) | null = null;

  /** Current project ID — set by the binding layer before module execution */
  projectId = 0;

  /**
   * Load a module from an assets path.
   * The path should be relative to the app root, e.g., 'assets/local_wasm/demo-arrow'.
   * The directory must contain module.js (instantiation mode) and module.core.wasm.
   */
  async loadModule(assetPath: string): Promise<LoadedModule> {
    // Dynamic import of the jco-transpiled module (instantiation mode)
    // @ts-ignore - dynamic path intentional for runtime module loading
    const moduleJs = await import(
      /* webpackIgnore: true */
      /* @vite-ignore */
      `/${assetPath}/module.js`
    );

    // Per-module storage for host-arrow and host-files
    const tables = new Map<string, Uint8Array>();
    const files = new Map<string, Uint8Array>();

    // Shared references for closures
    const variables = this.variables;
    const getLogCallback = () => this.logCallback;

    // Build imports object with WASI stubs and real host imports
    const imports = {
      ...createWasiStubs(),
      'enlighten:module/host-arrow': {
        readTable(name: string): Uint8Array {
          const data = tables.get(name);
          if (!data) throw new Error(`Table '${name}' not found`);
          return data;
        },
        writeTable(name: string, data: Uint8Array): void {
          tables.set(name, new Uint8Array(data));
        },
      },
      'enlighten:module/host-files': {
        readFile(path: string): Uint8Array {
          const data = files.get(path);
          if (!data) throw new Error(`File '${path}' not found`);
          return data;
        },
        readFileChunk(path: string, offset: bigint, length: number): Uint8Array {
          const data = files.get(path);
          if (!data) throw new Error(`File '${path}' not found`);
          const dataLength = BigInt(data.length);
          if (offset < 0n || offset >= dataLength || length <= 0) {
            return new Uint8Array(0);
          }
          const start = Number(offset);
          const end = Math.min(start + length, data.length);
          return new Uint8Array(data.buffer, data.byteOffset + start, end - start);
        },
        fileSize(path: string): bigint {
          const data = files.get(path);
          if (!data) throw new Error(`File '${path}' not found`);
          return BigInt(data.length);
        },
        writeFile(path: string, data: Uint8Array): void {
          files.set(path, new Uint8Array(data));
        },
        listFiles(prefix: string): string[] {
          return [...files.keys()].filter(k => k.startsWith(prefix));
        },
      },
      'enlighten:module/host-log': {
        log(level: string, message: string): void {
          getLogCallback()(level, message);
        },
      },
      'enlighten:module/host-variables': {
        getVariable(name: string): string | undefined {
          return variables.get(name);
        },
        setVariable(name: string, value: string): void {
          variables.set(name, value);
        },
        listVariables(): [string, string][] {
          return [...variables.entries()];
        },
      },
      'enlighten:module/host-api': {
        call: (service: string, method: string, argsJson: string): string => {
          if (!this.serviceCallFn) {
            throw new Error('host-api: service call not configured');
          }
          if (!this.projectId || this.projectId <= 0) {
            throw new Error(`host-api: invalid projectId '${this.projectId}'`);
          }
          return this.serviceCallFn(
            `${API_BASE}/project/${this.projectId}/service-call/${encodeURIComponent(service)}/${encodeURIComponent(method)}`,
            argsJson,
          );
        },
      },
    };

    // getCoreModule callback — fetches and compiles .wasm files
    const baseUrl = `/${assetPath}/`;
    const getCoreModule = async (path: string): Promise<WebAssembly.Module> => {
      const cacheKey = `${assetPath}/${path}`;
      if (this.moduleCache.has(cacheKey)) {
        return this.moduleCache.get(cacheKey)!;
      }
      const url = `${baseUrl}${path}`;
      const response = await fetch(url);
      // deepcode ignore javascript/CodeInjection: compiles a first-party .wasm asset fetched from a same-origin relative path (app bundle), not remote user input
      const module = await WebAssembly.compileStreaming(response);
      this.moduleCache.set(cacheKey, module);
      return module;
    };

    // Instantiate the component
    const instance = await moduleJs.instantiate(getCoreModule, imports);

    const manifestJson = instance.getManifest();
    const manifest: ModuleManifest = JSON.parse(manifestJson);

    const loaded: LoadedModule = {
      manifest,
      getManifest: instance.getManifest,
      execute: instance.execute,
      tables,
      files,
    };

    this.modules.set(manifest.name, loaded);
    console.log(`[WasmModuleService] Loaded: ${manifest.name}@${manifest.version} (${manifest.functions.length} functions)`);
    return loaded;
  }

  /** Get a loaded module by name */
  getModule(name: string): LoadedModule | undefined {
    return this.modules.get(name);
  }

  /** List all loaded module manifests */
  listModules(): ModuleManifest[] {
    return [...this.modules.values()].map(m => m.manifest);
  }

  /** Execute a function on a loaded module */
  executeFunction(moduleName: string, functionName: string, argsJson: string): string {
    const mod = this.modules.get(moduleName);
    if (!mod) {
      throw new Error(`Module '${moduleName}' not loaded`);
    }
    return mod.execute(functionName, argsJson);
  }
}
